import { languageQueryInjector } from '@/util/languageQueryInjector'
import queryInjector from '@/util/queryInjector'
import { isOnStaging, isProductionApi } from '@/util/helpers'
import { getLocalePrefix, pathnameWithoutLocalePrefix } from '@/util/localePath'

const addPrefix = value => (value ? `/${value}` : '')
const addKeyPrefix = (element, key) => addPrefix(element[key])

export const keepQueryString = pathname => languageQueryInjector(pathname)

// User account / Wallet / Notifications / Profile:
export const activityUrl = () => '/n/notifications'
export const accountUrl = () => '/a'
export const walletUrl = () => '/p/wallet'
export const userUrl = user => `/${user.username}`
export const kycUrl = () => '/m/kyc'
export const brazilianCampainUrl = () => '/riocriptoday/'
export const adminPanelSeeUserSummaryUrl = userId => {
  return isOnStaging()
    ? `https://api-staging.futuur.com/____/reports/user_summary/${userId}/`
    : `https://api.futuur.com/____/reports/user_summary/${userId}/`
}

// Real money / Cash-in / Withdraw:
export const redeemCode = () => '/m/redeem-code'
export const locationErrorUrl = () => `/p/location-not-allowed`
export const cashInConfirmationUrl = () => '/m/cash-in/confirmation'
export const buyBitcoinInfoUrl = () => '/m/how-to-buy-bitcoin'
export const depositStatus = () => `/astropay/deposit_status/:depositId`
export const withdrawStatus = () => `/astropay/cashout_status/:merchant_cashout_id/`
export const depositUrl = (currencyId, position) => {
  const params = new URLSearchParams()
  if (currencyId) params.set('currency', currencyId)
  if (position) params.set('position', position)
  const query = params.toString()
  return query ? `/m/cash-in?${query}` : '/m/cash-in'
}

export const withdrawUrl = currencyId => {
  if (currencyId) {
    return '/m/withdraw?currency=' + currencyId
  }

  return '/m/withdraw'
}

export const withdrawEmailReceivedUrl = () => {
  return '/m/withdraw/email-received'
}

export const withdrawValidationUrl = () => {
  return '/m/withdraw/validate'
}

export const recoverPassword = () => '/p/recover-password'
export const convertUrl = currencyId => `/convert?currency=${currencyId}`
// Settings
export const emailUpdateUrl = () => `/p/email-update`
export const personalInfoUrl = () => `/p/personal-info-update`
export const passwordUpdateUrl = () => '/p/update-password'

// Feed / Questions / Category
export const feedUrl = () => '/'
export const betUrlById = id => `/prediction/${id}`
export const questionDetailUrlById = questionId => `/q/${questionId}`
export const questionDetailUrl = question => `/q/${question.id}${addKeyPrefix(question, 'slug')}`
export const questionHistoryUrl = question => `${questionDetailUrl(question)}/history`
export const categoryListUrl = category => {
  if (!category) return '/markets/'

  // If category is part of a breadcrumb path, build the path up to this category
  if (Array.isArray(category)) {
    const pathSegments = category.map(cat => cat.slug)
    return `/markets/${pathSegments.join('/')}/${category[category.length - 1].id}`
  }

  // Fallback to the original behavior if no path is available
  return `/markets/${category.id}${addKeyPrefix(category, 'slug')}`
}
export const tagUrl = tagSlug => `/tags/${tagSlug}`
export const tagsListUrl = () => '/tags'
export const sellBetUrl = (questionId, betId) => `/m/${questionId}/sell/${betId}`
export const adminPanelEditMarketUrl = marketId => {
  return isOnStaging()
    ? `https://api-staging.futuur.com/____/wager/question/${marketId}/change/`
    : `https://api.futuur.com/____/wager/question/${marketId}/change/`
}
export const marketsUrl = () => `/markets/`
/** Brazil 2026 election landing (`src/constants/brazilElections.js` owns the path). */
export const brazilElectionsUrl = () => '/elections/brazil-2026'
/**
 * Where the election banner sends people to start trading. The category id only
 * exists on the production database, so anywhere else this stays on the landing
 * page, which builds itself from whatever markets that environment has.
 */
export const brazilElectionsMarketsUrl = () =>
  isProductionApi() ? '/markets/politics/2026-election/3508' : `${brazilElectionsUrl()}#duel`
export const rebateProgramUrl = (language = 'en') =>
  String(language).toLowerCase().startsWith('pt')
    ? 'https://help.futuur.com/programa-de-marker-rebate'
    : 'https://help.futuur.com/Market-Maker-Rebate-34f88d28324b80fba735f87ba4d1a32c'

// Auth / SignUp / Confirmation
export const loginUrl = clearToken => '/l/login' + (clearToken ? '?clearToken=true' : '')
/** Login modal alias (legacy “register” URL that still shows ViewLogin). */
export const loginRegisterUrl = () => '/l/register'
export const loginResetUrl = () => '/l/reset'
/**
 * Register CTAs open the login modal: Magic email OTP signs in and creates the
 * account in one flow, so there is no separate registration path to send them to.
 */
export const signupUrl = () => loginUrl()
/** The standalone `ViewSignup` wizard, still routed for its post-login steps. */
export const signupFormUrl = () => '/l/signup'
export const logoutUrl = () => '/l/logout'
export const emailConfirmationRequiredUrl = () => '/p/verify-your-email'
export const setUserNameAfterSignIn = () => '/p/complete-your-profile'
export const emailConfirmedUrl = () => '/p/confirmed-email'

// Terms / Policies
export const termsUrl = () => 'https://help.futuur.com/Terms-of-Service-a2f757178971446cba1402b3cdc9a799'
export const privacyPolicyUrl = () => 'https://help.futuur.com/Privacy-Policy-fbd0af05edba4027b62780f8a21a8ae6'
export const contestRulesUrl = () => '/i/contest_rules' 

// Other
export const communityUrl = () => import.meta.env.VITE_COMMUNITY_URL
export const notFoundUrl = () => '/i/not-found'
export const feedbackUrl = topic => (topic == null ? '/i/feedback' : `/i/feedback/?topic=${topic}`)

// Other methods
export const isLoginUrl = pathname => pathname.startsWith('/l/')
export const isLoginProfileUrl = pathname => pathname.startsWith('/l/profile')
export const isFeedPage = pathname => pathname.startsWith(feedUrl())
export const isCategoryPage = pathname => pathname.startsWith('/q/category')
export const isLocationPublic = pathname => {
  return !(
    pathname.startsWith('/account') ||
    pathname.startsWith('/settings') ||
    pathname.startsWith('/a/notifications') ||
    pathname.startsWith('/a/following')
  )
}

// Social / Leaderboard
export const leaderboardUrl = query => queryInjector('/c/leaderboard', query)

export const landingUrl = () => '/'
export const howItWorksUrl = () => '/i/how-it-works/'
export const faqUrl = () => 'https://help.futuur.com'
export const faqUrlBrl = () => 'https://help.futuur.com/Futuur-FAQ-PT-BR-1c9755229e6f466e839b6f932d328766'
export const jobs = () => 'https://futuur1.notion.site/Futuur-is-hiring-6b49e93eb58a4bfa8e9099a786140e94'
export const publicApiDocs = () => 'https://docs.futuur.com'

export const isLandingUrl = pathname => pathname === landingUrl()

export const settingsUrl = setting => (setting ? `/a/settings/${setting}` : `/a/settings`)
export const userUrlByUsername = username => `/${username}`
/**
 * Swaps the username in a profile path (`/old`, `/pt-br/old`, `/old/followers/`, …).
 * Null when the path is not one of that user's profile routes.
 */
export const replaceProfileUsername = (pathname, oldUsername, newUsername) => {
  if (!pathname || !oldUsername || !newUsername) return null
  const escaped = oldUsername.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  const match = pathnameWithoutLocalePrefix(pathname).match(
    new RegExp(`^/${escaped}(/(?:followers|following|follow))?/?$`, 'i')
  )
  if (!match) return null
  const trailingSlash = pathname.endsWith('/') ? '/' : ''
  return `${getLocalePrefix(pathname)}/${newUsername}${match[1] || ''}${trailingSlash}`
}
export const publicFollowUrl = user => `${userUrl(user)}/follow/`
export const followersUrl = user => `${userUrl(user)}/followers/`
export const followingUrl = user => `${userUrl(user)}/following/`

export const canonicalUserUrl = user => `${import.meta.env.VITE_FRONT_URL}${user.username}`
export const canonicalFollowersUrl = username => `${import.meta.env.VITE_FRONT_URL}${username}/followers/`
export const canonicalFollowingUrl = username => `${import.meta.env.VITE_FRONT_URL}${username}/following/`

const BASE_PATH = import.meta.env.VITE_SHARE_PATH || import.meta.env.VITE_FRONT_URL?.slice(0, -1) || ''

export const inviteUrl = query => queryInjector('/c/invite', query)
export const inviteEmailUrl = query => queryInjector('/c/invite/email', query)
export const inviteManualUrl = query => queryInjector('/c/invite/manual', query)
export const invitedUrl = query => queryInjector(`${BASE_PATH}/`, query)

export const facebookUrl = () => 'https://www.facebook.com/futuurHQ/'
export const twitterUrl = () => 'https://twitter.com/futuurHQ'
export const youtubeUrl = () => 'https://www.youtube.com/channel/UCEJkPOzzJzlV8jeb5mKRHeQ'
export const substackUrl = () => 'https://futuur.substack.com/'
export const instagramUrl = () => 'https://www.instagram.com/futuurHQ'
export const discordUrl = () => 'https://discord.gg/BJMX5mRZT5'
export const helpCenterUrl = (language = 'en') =>
  language === 'en'
    ? 'https://help.futuur.com'
    : 'https://help.futuur.com/Futuur-FAQ-PT-BR-1c9755229e6f466e839b6f932d328766'
 
export const marketMakerDocumentationUrl = () => 'https://help.futuur.com/Market-Maker-Rebate-34f88d28324b80fba735f87ba4d1a32c'
